import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Alert,
  FormControlLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import { convertElevation } from '../pages/WorkoutAnalysisV2';
import { generateWorkout, downloadZwiftWorkout, downloadFitWorkout, getUserProfile } from '../api/client';

const WorkoutGeneratorAction = ({ workoutData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const chunks = workoutData?.chunks || [];

  const [selectedMetrics, setSelectedMetrics] = useState(['power', 'altitude']);

  // Selection state (what's shown on the chart) - time-based in seconds
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [maxTime, setMaxTime] = useState(0);
  const [selectionStep, setSelectionStep] = useState(0); // 0=no selection, 1=start selected, 2=complete

  // Input field values (strings for proper editing - not applied to chart until user clicks Apply)
  const [startTimeInput, setStartTimeInput] = useState('0:00');
  const [endTimeInput, setEndTimeInput] = useState('0:00');
  const [hasStartPendingChanges, setHasStartPendingChanges] = useState(false);
  const [hasEndPendingChanges, setHasEndPendingChanges] = useState(false);

  // Workout parameters
  const [ftp, setFtp] = useState(200); // Functional Threshold Power in watts
  const [includeWarmupCooldown, setIncludeWarmupCooldown] = useState(true);
  const [warmupCooldownDuration, setWarmupCooldownDuration] = useState(600); // 10 minutes in seconds
  const [warmupCooldownPower, setWarmupCooldownPower] = useState(0);
  const [powerRatio, setPowerRatio] = useState(1.0);
  const [stepPower, setStepPower] = useState(20);

  // Workout preview state
  const [workoutSteps, setWorkoutSteps] = useState([]);
  const [workoutTss, setWorkoutTss] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  const pendingChanges = hasStartPendingChanges || hasEndPendingChanges;

  // Calculate duration from start and end
  const duration = Math.max(0, endTime - startTime);

  // Load user's FTP from profile
  useEffect(() => {
    const loadUserFtp = async () => {
      try {
        const userProfile = await getUserProfile();
        if (userProfile && userProfile.ftp) {
          setFtp(userProfile.ftp);
        }
      } catch (error) {
        // User not authenticated or error fetching profile - use default FTP
        console.log('Could not load user FTP, using default:', error.message);
      }
    };
    loadUserFtp();
  }, []);

  // Calculate total time and set initial warmup/cooldown power based on FTP
  useEffect(() => {
    if (chunks.length > 0) {
      const totalTimeSeconds = chunks.reduce((sum, chunk) => sum + chunk.duration, 0);

      // Calculate warm-up and cooldown power as 60% of FTP, rounded to nearest 10
      const targetPower = Math.round((ftp * 0.60) / 10) * 10;

      setMaxTime(totalTimeSeconds);
      setStartTime(0);
      setEndTime(totalTimeSeconds);
      setStartTimeInput(formatTime(0));
      setEndTimeInput(formatTime(totalTimeSeconds));
      setWarmupCooldownPower(targetPower);
      setSelectionStep(2); // Show full workout by default
    }
  }, [chunks, ftp]);

  // Format time as HH:MM:SS
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Parse time string MM:SS or HH:MM:SS to seconds
  const parseTime = (timeStr) => {
    const parts = timeStr.split(':').map(p => parseInt(p, 10));
    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    } else if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    return 0;
  };

  const chartLabels = {
    elevationLabel: t('altitudeLineName', 'Altitude'),
    speedLabel: t('speedLineName', 'Speed'),
    gradientLabel: t('gradientLineName', 'Gradient'),
    powerLabel: t('powerLineName', 'Power'),
    timeLabel: t('time', 'Time')
  };

  // Generate time-based chart data
  const chartData = (() => {
    const TIME_INTERVAL = 5; // 5-second intervals
    let cumulativeTime = 0;
    const chunkTimeRanges = chunks.map((chunk, index) => {
      const startTime = cumulativeTime;
      cumulativeTime += chunk.duration;
      const endTime = cumulativeTime;
      return { chunk, startTime, endTime, index };
    });

    const totalDuration = cumulativeTime;
    const result = [];

    for (let t = 0; t <= totalDuration; t += TIME_INTERVAL) {
      const currentChunkData = chunkTimeRanges.find(cr => t >= cr.startTime && t <= cr.endTime);

      if (currentChunkData) {
        const currentChunk = currentChunkData.chunk;

        result.push({
          time: t,
          timeLabel: formatTime(t),
          altitude: parseFloat(currentChunk.avg_altitude.toFixed(1)),
          power: parseFloat(currentChunk.avg_power.toFixed(0)),
          gradient: parseFloat(currentChunk.avg_gradient.toFixed(2)),
          speed: parseFloat(currentChunk.avg_speed.toFixed(1))
        });
      }
    }

    return result;
  })();

  const handleChartClick = (data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const clickedTime = data.activePayload[0].payload.time;

      if (selectionStep === 0 || selectionStep === 2) {
        // First click or reset - set start point
        setStartTime(clickedTime);
        setStartTimeInput(formatTime(clickedTime));
        setSelectionStep(1);
        setHasStartPendingChanges(false);
        setHasEndPendingChanges(false);
      } else if (selectionStep === 1) {
        // Second click - set end point
        if (clickedTime > startTime) {
          setEndTime(clickedTime);
          setEndTimeInput(formatTime(clickedTime));
          setSelectionStep(2);
        } else {
          // Clicked before start, swap them
          setEndTime(startTime);
          setEndTimeInput(formatTime(startTime));
          setStartTime(clickedTime);
          setStartTimeInput(formatTime(clickedTime));
          setSelectionStep(2);
        }
        setHasStartPendingChanges(false);
        setHasEndPendingChanges(false);
      }
    }
  };

  const handleStartTimeChange = (value) => {
    setStartTimeInput(value);
    setHasStartPendingChanges(true);
  };

  const handleEndTimeChange = (value) => {
    setEndTimeInput(value);
    setHasEndPendingChanges(true);
  };

  // Helper function to find the closest time value in the chart data
  const findClosestDataPoint = (targetTime) => {
    if (chartData.length === 0) return targetTime;

    let closest = chartData[0].time;
    let minDiff = Math.abs(targetTime - closest);

    for (let i = 1; i < chartData.length; i++) {
      const diff = Math.abs(targetTime - chartData[i].time);
      if (diff < minDiff) {
        minDiff = diff;
        closest = chartData[i].time;
      }
    }

    return closest;
  };

  const handleApplyManualSelection = () => {
    const parsedStart = parseTime(startTimeInput);
    const parsedEnd = parseTime(endTimeInput);

    if (isNaN(parsedStart) || isNaN(parsedEnd)) {
      alert('Please enter valid time values for both start and end (format: MM:SS or HH:MM:SS).');
      return;
    }

    // Validate and clamp values
    const clampedStart = Math.max(0, Math.min(parsedStart, maxTime));
    const clampedEnd = Math.max(clampedStart, Math.min(parsedEnd, maxTime));

    // Snap to closest data points
    const newStart = findClosestDataPoint(clampedStart);
    const newEnd = findClosestDataPoint(clampedEnd);

    // Update chart selection
    setStartTime(newStart);
    setEndTime(newEnd);
    setSelectionStep(2);
    setHasStartPendingChanges(false);
    setHasEndPendingChanges(false);

    // Format the input fields with snapped values
    setStartTimeInput(formatTime(newStart));
    setEndTimeInput(formatTime(newEnd));
  };

  const handleResetSelection = () => {
    setStartTime(0);
    setEndTime(maxTime);
    setStartTimeInput(formatTime(0));
    setEndTimeInput(formatTime(maxTime));
    setSelectionStep(2);
    setHasStartPendingChanges(false);
    setHasEndPendingChanges(false);
  };

  const handleGenerateWorkout = async () => {
    if (!workoutData?.analysis_session_id) {
      alert('Error: Analysis session ID not found. Please re-analyze your workout.');
      return;
    }

    if (selectionStep !== 2) {
      alert('Please select both start and end points before generating workout.');
      return;
    }

    try {
      const data = await generateWorkout({
        analysisSessionId: workoutData.analysis_session_id,
        startTime,
        endTime,
        ftp,
        includeWarmup: includeWarmupCooldown,
        warmupDuration: warmupCooldownDuration,
        warmupPower: warmupCooldownPower,
        includeCooldown: includeWarmupCooldown,
        cooldownDuration: warmupCooldownDuration,
        cooldownPower: warmupCooldownPower,
        powerRatio,
        stepPower
      });

      setWorkoutSteps(data.steps);
      setWorkoutTss(data.tss || 0);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating workout:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error generating workout: ${errorMessage}`);
    }
  };

  const handleDownloadWorkout = async () => {
    if (!workoutData?.analysis_session_id) {
      alert('Error: Analysis session ID not found.');
      return;
    }

    try {
      const response = await downloadZwiftWorkout({
        analysisSessionId: workoutData.analysis_session_id,
        startTime,
        endTime,
        ftp,
        includeWarmup: includeWarmupCooldown,
        warmupDuration: warmupCooldownDuration,
        warmupPower: warmupCooldownPower,
        includeCooldown: includeWarmupCooldown,
        cooldownDuration: warmupCooldownDuration,
        cooldownPower: warmupCooldownPower,
        powerRatio,
        stepPower
      });

      // Get the filename from the response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'mkpace_bike_workout.zwo';
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Download the file
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Zwift workout downloaded successfully:', filename);
    } catch (error) {
      console.error('Error downloading workout:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error downloading workout: ${errorMessage}`);
    }
  };

  const handleDownloadFitWorkout = async () => {
    if (!workoutData?.analysis_session_id) {
      alert('Error: Analysis session ID not found.');
      return;
    }

    try {
      const response = await downloadFitWorkout({
        analysisSessionId: workoutData.analysis_session_id,
        startTime,
        endTime,
        ftp,
        includeWarmup: includeWarmupCooldown,
        warmupDuration: warmupCooldownDuration,
        warmupPower: warmupCooldownPower,
        includeCooldown: includeWarmupCooldown,
        cooldownDuration: warmupCooldownDuration,
        cooldownPower: warmupCooldownPower,
        powerRatio,
        stepPower
      });

      // Get the filename from the response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'mkpace_bike_workout.fit';
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Download the file
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('FIT workout downloaded successfully:', filename);
    } catch (error) {
      console.error('Error downloading FIT workout:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Error downloading FIT workout: ${errorMessage}`);
    }
  };

  const workoutColumns = [
    { field: 'id', headerName: '#', width: 70 },
    {
      field: 'type',
      headerName: t('typeColumn', 'Type'),
      width: 130,
      valueFormatter: (value) => {
        return t(`workoutTypes.${value}`, value);
      }
    },
    {
      field: 'duration',
      headerName: t('durationColumn', 'Duration'),
      width: 130,
      valueFormatter: (value) => formatTime(value)
    },
    { field: 'power', headerName: t('powerColumn', 'Power (W)'), width: 130 },
    {
      field: 'position',
      headerName: 'Position',
      width: 150,
      valueFormatter: (value, row) => {
        const positionLabels = {
          'tt_aero': 'TT Aero',
          'tt_base': 'TT Base',
          'road_drops': 'Drops',
          'road_hoods': 'Hoods'
        };

        // Show "Any" for warmup and cooldown steps
        if (row.type === 'warmup' || row.type === 'cooldown') {
          return 'Any';
        }

        return positionLabels[value] || value || 'N/A';
      }
    },
  ];

  if (!chunks || chunks.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          {t('noWorkoutDataForGenerator', 'No workout data available for Zwift workout generation.')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <Typography variant="h5" color={colors.grey[100]} mb={3}>
        {t('generateWorkout', 'Generate Workout')}
      </Typography>

      {/* Selection Controls */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: colors.grey[800],
          borderRadius: '8px'
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="grid" gridTemplateColumns="1fr 1fr 100px" gap={2} alignItems="end">
            <TextField
              label={t('startTime', 'Start Time (MM:SS)')}
              value={startTimeInput}
              onChange={(e) => handleStartTimeChange(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleApplyManualSelection();
                }
              }}
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: colors.grey[300] },
                '& .MuiInputBase-input': { color: colors.grey[100] },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: hasStartPendingChanges ? colors.warnings.orange : colors.grey[600]
                }
              }}
            />
            <TextField
              label={t('endTime', 'End Time (MM:SS)')}
              value={endTimeInput}
              onChange={(e) => handleEndTimeChange(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleApplyManualSelection();
                }
              }}
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: colors.grey[300] },
                '& .MuiInputBase-input': { color: colors.grey[100] },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: hasEndPendingChanges ? colors.warnings.orange : colors.grey[600]
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleApplyManualSelection}
              disabled={!pendingChanges}
              size="small"
              sx={{
                backgroundColor: colors.blueAccent[600],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.blueAccent[700]
                },
                '&:disabled': {
                  backgroundColor: colors.grey[700],
                  color: colors.grey[500]
                }
              }}
            >
              {t('apply', 'Apply')}
            </Button>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" flexDirection="column">
              <Typography variant="caption" color={colors.grey[400]}>
                {t('duration', 'Duration')}
              </Typography>
              <Typography variant="h6" color={colors.blueAccent[400]} fontWeight="bold">
                {formatTime(duration)}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={handleResetSelection}
              size="small"
              sx={{
                color: colors.grey[100],
                borderColor: colors.grey[600],
                '&:hover': {
                  borderColor: colors.grey[400],
                  backgroundColor: colors.grey[800]
                }
              }}
            >
              {t('resetToFullWorkout', 'Reset to Full Workout')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Chart Controls */}
      <Box mb={2}>
        <FormControl sx={{ minWidth: isMobile ? '100%' : 300, width: isMobile ? '100%' : 'auto' }}>
          <InputLabel sx={{ color: colors.grey[300] }}>{t('selectMetrics', 'Select Metrics')}</InputLabel>
          <Select
            multiple
            value={selectedMetrics}
            onChange={(e) => setSelectedMetrics(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label={t('selectMetrics', 'Select Metrics')} />}
            renderValue={(selected) => selected.join(', ')}
            sx={{
              color: colors.grey[100],
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
            }}
          >
            <MenuItem value="power">
              <Checkbox checked={selectedMetrics.indexOf('power') > -1} />
              <ListItemText primary={chartLabels.powerLabel} />
            </MenuItem>
            <MenuItem value="altitude">
              <Checkbox checked={selectedMetrics.indexOf('altitude') > -1} />
              <ListItemText primary={chartLabels.elevationLabel} />
            </MenuItem>
            <MenuItem value="speed">
              <Checkbox checked={selectedMetrics.indexOf('speed') > -1} />
              <ListItemText primary={chartLabels.speedLabel} />
            </MenuItem>
            <MenuItem value="gradient">
              <Checkbox checked={selectedMetrics.indexOf('gradient') > -1} />
              <ListItemText primary={chartLabels.gradientLabel} />
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Time-Based Chart */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: colors.grey[800],
          borderRadius: '8px'
        }}
      >
        <Box mb={2} p={2} sx={{ backgroundColor: colors.primary[900], borderRadius: 1, border: `1px solid ${colors.primary[700]}` }}>
          <Typography variant="body2" color={colors.grey[100]} fontWeight="bold">
            {selectionStep === 0 && `👆 ${t('clickToStartSelection', 'Click on the chart to start selecting a range')}`}
            {selectionStep === 1 && `✅ ${t('startPointSet', 'Start point set! Click again to set the end point')}`}
            {selectionStep === 2 && `✓ ${t('selectedRange', { start: formatTime(startTime), end: formatTime(endTime), duration: formatTime(duration), defaultValue: 'Selected: {{start}} to {{end}} ({{duration}})' })}`}
          </Typography>
        </Box>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            onClick={handleChartClick}
            style={{ cursor: 'crosshair' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
            <XAxis
              dataKey="time"
              stroke={colors.grey[100]}
              label={{ value: chartLabels.timeLabel, position: 'insideBottom', offset: -5, fill: colors.grey[300] }}
              tickFormatter={(value) => formatTime(value)}
            />
            <YAxis
              stroke={colors.grey[100]}
              label={{
                value: t('valuesYAxis', 'Values'),
                angle: -90,
                position: 'insideLeft',
                fill: colors.grey[300]
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: colors.grey[900],
                border: `1px solid ${colors.grey[700]}`,
                borderRadius: '4px'
              }}
              labelStyle={{ color: colors.grey[100] }}
              itemStyle={{ color: colors.blueAccent[400] }}
              labelFormatter={(value) => `Time: ${formatTime(value)}`}
            />
            <Legend wrapperStyle={{ color: colors.grey[100] }} verticalAlign="top" height={50} />

            {/* Selection Markers */}
            {selectionStep >= 1 && (
              <ReferenceLine
                x={startTime}
                stroke={colors.greenAccent[500]}
                strokeWidth={3}
                label={{ value: t('startLabel', 'Start'), position: 'top', fill: colors.greenAccent[500], fontSize: 14, fontWeight: 'bold' }}
              />
            )}
            {selectionStep === 2 && (
              <>
                <ReferenceArea
                  x1={startTime}
                  x2={endTime}
                  strokeOpacity={0.3}
                  fill={colors.blueAccent[600]}
                  fillOpacity={0.3}
                />
                <ReferenceLine
                  x={endTime}
                  stroke={colors.redAccent[500]}
                  strokeWidth={3}
                  label={{ value: t('endLabel', 'End'), position: 'top', fill: colors.redAccent[500], fontSize: 14, fontWeight: 'bold' }}
                />
              </>
            )}

            {selectedMetrics.includes('power') && (
              <Line
                type="natural"
                dataKey="power"
                stroke={colors.chartColors[4]}
                dot={false}
                strokeWidth={2}
                name={chartLabels.powerLabel}
              />
            )}
            {selectedMetrics.includes('altitude') && (
              <Line
                type="natural"
                dataKey="altitude"
                stroke={colors.chartColors[1]}
                dot={false}
                strokeWidth={2}
                name={chartLabels.elevationLabel}
              />
            )}
            {selectedMetrics.includes('speed') && (
              <Line
                type="natural"
                dataKey="speed"
                stroke={colors.chartColors[2]}
                dot={false}
                strokeWidth={2}
                name={chartLabels.speedLabel}
              />
            )}
            {selectedMetrics.includes('gradient') && (
              <Line
                type="natural"
                dataKey="gradient"
                stroke={colors.chartColors[3]}
                dot={false}
                strokeWidth={2}
                name={chartLabels.gradientLabel}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* Workout Parameters */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: colors.grey[800],
          borderRadius: '8px'
        }}
      >
        <Typography variant="h6" color={colors.grey[100]} mb={2}>
          {t('workoutParameters', 'Workout Parameters')}
        </Typography>

        <Box display="flex" flexDirection="column" gap={3}>
          {/* FTP (Functional Threshold Power) */}
          <TextField
            label={t('ftpLabel', 'FTP (Functional Threshold Power)')}
            type="number"
            value={ftp}
            onChange={(e) => setFtp(parseInt(e.target.value, 10))}
            size="small"
            inputProps={{ step: 5, min: 50, max: 500 }}
            helperText={t('ftpHelperText', 'Your threshold power in watts - used to calculate workout intensities')}
            sx={{
              '& .MuiInputLabel-root': { color: colors.grey[300] },
              '& .MuiInputBase-input': { color: colors.grey[100] },
              '& .MuiFormHelperText-root': { color: colors.grey[400] }
            }}
          />

          {/* Warmup and Cooldown section - with visual grouping */}
          <Box
            sx={{
              p: 2,
              borderRadius: '4px',
              border: `1px solid ${colors.grey[600]}`
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeWarmupCooldown}
                  onChange={(e) => setIncludeWarmupCooldown(e.target.checked)}
                  sx={{ color: colors.grey[300] }}
                />
              }
              label={t('includeWarmupCooldown', 'Include Warmup and Cooldown')}
              sx={{ color: colors.grey[100], mb: 2 }}
            />
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} ml={4}>
              <TextField
                label={t('durationMinutes', 'Duration (minutes)')}
                type="number"
                value={warmupCooldownDuration / 60}
                onChange={(e) => setWarmupCooldownDuration(parseFloat(e.target.value) * 60)}
                size="small"
                inputProps={{ step: 1, min: 1 }}
                sx={{
                  '& .MuiInputLabel-root': { color: colors.grey[300] },
                  '& .MuiInputBase-input': { color: colors.grey[100] }
                }}
              />
              <TextField
                label={t('powerWarmupCooldownRecovery', 'Power (W) - Warmup, Cooldown & Active Recovery')}
                type="number"
                value={warmupCooldownPower}
                onChange={(e) => setWarmupCooldownPower(parseInt(e.target.value, 10))}
                size="small"
                inputProps={{ step: 10, min: 0 }}
                sx={{
                  '& .MuiInputLabel-root': { color: colors.grey[300] },
                  '& .MuiInputBase-input': { color: colors.grey[100] }
                }}
              />
            </Box>
          </Box>

          {/* Advanced Parameters section */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          {/* Power Ratio */}
          <TextField
            label={t('powerRatio', 'Power Ratio')}
            type="number"
            value={powerRatio}
            onChange={(e) => setPowerRatio(parseFloat(e.target.value))}
            size="small"
            inputProps={{ step: 0.1, min: 0.1, max: 2.0 }}
            helperText={t('powerRatioHelperText', 'Multiplier for all power values')}
            sx={{
              '& .MuiInputLabel-root': { color: colors.grey[300] },
              '& .MuiInputBase-input': { color: colors.grey[100] },
              '& .MuiFormHelperText-root': { color: colors.grey[400] }
            }}
          />

          {/* Step Power */}
          <TextField
            label={t('stepPower', 'Step Power (W)')}
            type="number"
            value={stepPower}
            onChange={(e) => setStepPower(parseInt(e.target.value, 10))}
            size="small"
            inputProps={{ step: 5, min: 5 }}
            helperText={t('stepPowerHelperText', 'Power range to group chunks')}
            sx={{
              '& .MuiInputLabel-root': { color: colors.grey[300] },
              '& .MuiInputBase-input': { color: colors.grey[100] },
              '& .MuiFormHelperText-root': { color: colors.grey[400] }
            }}
          />
          </Box>
        </Box>
      </Paper>

      {/* Generate Button */}
      <Box display="flex" gap={2}>
        <Button
          variant="contained"
          startIcon={<FitnessCenterIcon />}
          onClick={handleGenerateWorkout}
          disabled={selectionStep !== 2}
          sx={{
            backgroundColor: colors.greenAccent[600],
            color: colors.grey[100],
            '&:hover': {
              backgroundColor: colors.greenAccent[700]
            },
            '&:disabled': {
              backgroundColor: colors.grey[700],
              color: colors.grey[500]
            }
          }}
        >
          {t('generateWorkout', 'Generate Workout')}
        </Button>
      </Box>

      {/* Workout Preview */}
      {showPreview && workoutSteps.length > 0 && (
        <Box mt={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              backgroundColor: colors.grey[800],
              borderRadius: '8px'
            }}
          >
            <Typography variant="h6" color={colors.grey[100]} mb={2}>
              {t('workoutPreview', 'Workout Preview')}
            </Typography>
            <Box height={400}>
              <DataGrid
                rows={workoutSteps}
                columns={workoutColumns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  '& .MuiDataGrid-root': {
                    border: 'none'
                  },
                  '& .MuiDataGrid-cell': {
                    color: colors.grey[100]
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: colors.grey[700],
                    color: colors.grey[100]
                  },
                  '& .MuiDataGrid-footerContainer': {
                    backgroundColor: colors.grey[700],
                    color: colors.grey[100]
                  }
                }}
              />
            </Box>

            {/* Workout Summary */}
            <Box
              mt={2}
              p={2}
              sx={{
                backgroundColor: colors.grey[700],
                borderRadius: '4px',
                border: `1px solid ${colors.grey[600]}`
              }}
            >
              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={3}>
                <Box>
                  <Typography variant="caption" color={colors.grey[400]}>
                    {t('totalWorkoutDuration', 'Total Workout Duration')}
                  </Typography>
                  <Typography variant="h6" color={colors.blueAccent[400]} fontWeight="bold">
                    {formatTime(workoutSteps.reduce((sum, step) => sum + step.duration, 0))}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color={colors.grey[400]}>
                    {t('averagePower', 'Average Power')}
                  </Typography>
                  <Typography variant="h6" color={colors.greenAccent[400]} fontWeight="bold">
                    {(() => {
                      const totalPowerTime = workoutSteps.reduce((sum, step) => sum + (step.power * step.duration), 0);
                      const totalDuration = workoutSteps.reduce((sum, step) => sum + step.duration, 0);
                      return totalDuration > 0 ? Math.round(totalPowerTime / totalDuration) : 0;
                    })()} W
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color={colors.grey[400]}>
                    TSS
                  </Typography>
                  <Typography
                    variant="h6"
                    color={
                      workoutTss < 70
                        ? colors.redAccent[300]
                        : workoutTss < 100
                          ? colors.redAccent[400]
                          : colors.redAccent[600]
                    }
                    fontWeight="bold"
                  >
                    {workoutTss}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Workout Power Chart */}
            <Box mt={3}>
              <Typography variant="h6" color={colors.grey[100]} mb={2}>
                {t('workoutPowerProfile', 'Workout Power Profile')}
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={(() => {
                    // Helper function to get altitude from original workout data with interpolation
                    // offsetTime is the time offset from the selected startTime of the crop
                    const getAltitudeForTime = (offsetTime) => {
                      // Map the offset time to the actual time in the original workout data
                      // by adding the selected startTime from the crop
                      const actualTime = startTime + offsetTime;

                      // Find the two closest data points to interpolate between
                      const sortedData = [...chartData].sort((a, b) => a.time - b.time);

                      // Find the point at or before this time
                      let beforeIndex = sortedData.findIndex(d => d.time > actualTime) - 1;
                      if (beforeIndex < 0) beforeIndex = 0;

                      const beforePoint = sortedData[beforeIndex];
                      const afterPoint = sortedData[beforeIndex + 1];

                      if (!beforePoint || beforePoint.altitude === null) return null;
                      if (!afterPoint || afterPoint.altitude === null) return beforePoint.altitude;

                      // Linear interpolation between the two points
                      const timeDiff = afterPoint.time - beforePoint.time;
                      const altDiff = afterPoint.altitude - beforePoint.altitude;
                      const timeOffset = actualTime - beforePoint.time;

                      if (timeDiff === 0) return beforePoint.altitude;

                      const interpolatedAlt = beforePoint.altitude + (altDiff * timeOffset / timeDiff);
                      return Math.round(interpolatedAlt * 10) / 10; // Round to 1 decimal
                    };

                    let cumulativeTime = 0;
                    let workoutDataTime = 0; // Time in the actual workout data (excluding warmup/cooldown)
                    const dataPoints = [];
                    const interpolationInterval = 30; // Sample altitude every 30 seconds

                    // Get the start and end altitude values for warmup/cooldown
                    let startAltitudeValue = null;
                    let endAltitudeValue = null;

                    if (includeWarmupCooldown) {
                      // Get the first altitude value from the main workout (at the selected startTime)
                      startAltitudeValue = getAltitudeForTime(0);
                      // Get the last altitude value from the main workout (at the selected endTime)
                      const mainWorkoutDuration = workoutSteps.slice(1, -1).reduce((sum, step) => sum + step.duration, 0);
                      endAltitudeValue = getAltitudeForTime(mainWorkoutDuration);
                    }

                    workoutSteps.forEach((step, index) => {
                      const stepStartTime = cumulativeTime;
                      const stepEndTime = cumulativeTime + step.duration;

                      // Determine if this is warmup or cooldown
                      const isWarmup = includeWarmupCooldown && index === 0;
                      const isCooldown = includeWarmupCooldown && index === workoutSteps.length - 1;

                      // For warmup and cooldown, add straight line with constant altitude
                      if (isWarmup) {
                        // Add start point with start altitude
                        dataPoints.push({
                          time: stepStartTime,
                          timeLabel: formatTime(stepStartTime),
                          power: step.power,
                          altitude: startAltitudeValue,
                          stepLabel: t(`workoutTypes.${step.type}`, step.type),
                          position: step.position || null
                        });

                        // Add end point with same start altitude
                        dataPoints.push({
                          time: stepEndTime,
                          timeLabel: formatTime(stepEndTime),
                          power: step.power,
                          altitude: startAltitudeValue,
                          stepLabel: t(`workoutTypes.${step.type}`, step.type),
                          position: step.position || null
                        });
                      } else if (isCooldown) {
                        // Add start point with end altitude
                        dataPoints.push({
                          time: stepStartTime,
                          timeLabel: formatTime(stepStartTime),
                          power: step.power,
                          altitude: endAltitudeValue,
                          stepLabel: t(`workoutTypes.${step.type}`, step.type),
                          position: step.position || null
                        });

                        // Add end point with same end altitude
                        dataPoints.push({
                          time: stepEndTime,
                          timeLabel: formatTime(stepEndTime),
                          power: step.power,
                          altitude: endAltitudeValue,
                          stepLabel: t(`workoutTypes.${step.type}`, step.type),
                          position: step.position || null
                        });
                      } else {
                        // For main workout steps, interpolate altitude at regular intervals
                        const numIntervals = Math.ceil(step.duration / interpolationInterval);

                        for (let i = 0; i <= numIntervals; i++) {
                          const intervalTime = stepStartTime + (i * interpolationInterval);
                          const time = i === numIntervals ? stepEndTime : Math.min(intervalTime, stepEndTime);

                          const offsetTime = workoutDataTime + (time - stepStartTime);
                          const altitude = getAltitudeForTime(offsetTime);

                          dataPoints.push({
                            time: time,
                            timeLabel: formatTime(time),
                            power: step.power,
                            altitude: altitude,
                            stepLabel: t(`workoutTypes.${step.type}`, step.type),
                            position: step.position || null
                          });

                          // Don't add duplicate end point if this is not the last step
                          if (i === numIntervals && index < workoutSteps.length - 1) {
                            break;
                          }
                        }

                        workoutDataTime += step.duration;
                      }

                      cumulativeTime = stepEndTime;
                    });

                    return dataPoints;
                  })()}
                  margin={{ top: 5, right: 60, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
                  <XAxis
                    dataKey="time"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    stroke={colors.grey[100]}
                    label={{ value: t('timeLabel', 'Time'), position: 'insideBottom', offset: -5, fill: colors.grey[300] }}
                    tickFormatter={(value) => formatTime(value)}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke={colors.grey[100]}
                    label={{
                      value: t('powerWatts', 'Power (W)'),
                      angle: -90,
                      position: 'insideLeft',
                      fill: colors.grey[300]
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke={colors.grey[100]}
                    label={{
                      value: t('altitudeLineName', 'Altitude'),
                      angle: 90,
                      position: 'insideRight',
                      fill: colors.grey[300]
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: colors.grey[900],
                      border: `1px solid ${colors.grey[700]}`,
                      borderRadius: '4px'
                    }}
                    labelStyle={{ color: colors.grey[100] }}
                    itemStyle={{ color: colors.blueAccent[400] }}
                    labelFormatter={(value, payload) => {
                      const positionLabels = { road_hoods: 'Hoods', road_drops: 'Drops', tt_aero: 'TT Aero', tt_base: 'TT Base' };
                      const pos = payload?.[0]?.payload?.position;
                      const posLabel = pos ? ` · ${positionLabels[pos] || pos}` : '';
                      return `${t('timeLabel', 'Time')}: ${formatTime(value)}${posLabel}`;
                    }}
                  />
                  <Legend wrapperStyle={{ color: colors.grey[100] }} verticalAlign="top" height={36} />
                  {/* Position bands — 3 aero levels mapped to warm amber shades */}
                  {(() => {
                    const positionColors = {
                      // Level 1 — upright/relaxed
                      road_hoods:   'rgba(255,210,80,0.10)',
                      tt_base:      'rgba(255,210,80,0.10)',
                      // Level 2 — medium aero
                      road_drops:   'rgba(255,150,40,0.13)',
                      road_aerobar: 'rgba(255,150,40,0.13)',
                      // Level 3 — full aero
                      tt_aero:      'rgba(220,90,20,0.16)'
                    };
                    const bands = [];
                    let t0 = 0;
                    workoutSteps.forEach(step => {
                      const t1 = t0 + step.duration;
                      if (step.position && positionColors[step.position]) {
                        bands.push(
                          <ReferenceArea
                            key={`pos-${t0}`}
                            yAxisId="left"
                            x1={t0}
                            x2={t1}
                            fill={positionColors[step.position]}
                            strokeOpacity={0}
                          />
                        );
                      }
                      t0 = t1;
                    });
                    return bands;
                  })()}
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="power"
                    stroke={colors.chartColors[4]}
                    strokeWidth={3}
                    dot={false}
                    name={t('powerWatts', 'Power (W)')}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="altitude"
                    stroke={colors.chartColors[1]}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    connectNulls={false}
                    name={t('altitudeLineName', 'Altitude')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            <Box mt={2} display="flex" gap={2}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadWorkout}
                sx={{
                  backgroundColor: colors.blueAccent[600],
                  color: colors.grey[100],
                  '&:hover': {
                    backgroundColor: colors.blueAccent[700]
                  }
                }}
              >
                {t('downloadZwiftWorkout', 'Download Zwift Workout')}
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadFitWorkout}
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: colors.grey[100],
                  '&:hover': {
                    backgroundColor: colors.greenAccent[700]
                  }
                }}
              >
                {t('downloadFitWorkout', 'Download FIT Workout')}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>{t('workoutInstructions', 'How to use')}</strong>
        </Typography>
        <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
          <li dangerouslySetInnerHTML={{ __html: t('workoutInstructionsStep1', '<strong>Select Range:</strong> Click on the chart to set start and end times, or enter times manually.') }}></li>
          <li dangerouslySetInnerHTML={{ __html: t('workoutInstructionsStep2', '<strong>Generate:</strong> Click "Generate Workout" to create the workout structure.') }}></li>
          <li dangerouslySetInnerHTML={{ __html: t('workoutInstructionsStep3', '<strong>Preview & Download:</strong> Review the workout steps in the table and download the workout file.') }}></li>
        </Typography>
      </Alert>
    </Box>
  );
};

export default WorkoutGeneratorAction;
